import React, { useCallback, useEffect, useRef, useState } from "react";
import { format, startOfDay, endOfDay, subDays, parseISO } from "date-fns";

import Paper from "@material-ui/core/Paper";
import Container from "@material-ui/core/Container";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import ButtonGroup from "@material-ui/core/ButtonGroup";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import { makeStyles } from "@material-ui/core/styles";

import PhoneInTalkIcon from "@material-ui/icons/PhoneInTalk";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import HeadsetMicIcon from "@material-ui/icons/HeadsetMic";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import SendIcon from "@material-ui/icons/Send";
import CallReceivedIcon from "@material-ui/icons/CallReceived";
import GroupAddIcon from "@material-ui/icons/GroupAdd";
import AlarmOnIcon from "@material-ui/icons/AlarmOn";
import TimerIcon from "@material-ui/icons/Timer";

import api from "../../services/api";
import openSocket from "../../services/socket-io";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

import AnnouncementCard from "../../components/AnnouncementCard";
import Chart from "./Chart";

const useStyles = makeStyles((theme) => ({
  container: {
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(4),
  },
  announcements: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  filters: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(3),
  },
  dateField: {
    width: 170,
  },
  card: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    minHeight: 128,
    padding: theme.spacing(2, 2.5),
    borderRadius: theme.borderRadius.medium,
    color: "#FFF",
    overflow: "hidden",
    boxShadow: theme.elevation.light,
  },
  cardTitle: {
    maxWidth: "70%",
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: "0.04em",
    lineHeight: 1.35,
    textTransform: "uppercase",
  },
  cardValue: {
    fontSize: 30,
    fontWeight: 500,
    lineHeight: 1.2,
  },
  cardIcon: {
    position: "absolute",
    top: "50%",
    right: theme.spacing(2),
    transform: "translateY(-50%)",
    fontSize: 64,
    opacity: 0.9,
  },
  paper: {
    padding: theme.spacing(2),
    borderRadius: theme.borderRadius.medium,
    overflowX: "auto",
  },
  chartPaper: {
    padding: theme.spacing(2),
    borderRadius: theme.borderRadius.medium,
    display: "flex",
    flexDirection: "column",
    height: 260,
  },
  sectionTitle: {
    marginBottom: theme.spacing(1),
  },
  statusDot: {
    display: "inline-block",
    width: 10,
    height: 10,
    marginRight: theme.spacing(1),
    borderRadius: theme.borderRadius.pill,
  },
  online: {
    backgroundColor: theme.palette.success.main,
  },
  offline: {
    backgroundColor: theme.palette.text.disabled,
  },
}));

// Tons de violeta/índigo/fúcsia, do mais escuro ao mais claro
const gradients = {
  inAttendance: ["#6D28D9", "#4C1D95"],
  waiting: ["#7C3AED", "#5B21B6"],
  onlineUsers: ["#8B5CF6", "#6D28D9"],
  closed: ["#4338CA", "#312E81"],
  messagesSent: ["#6366F1", "#4338CA"],
  messagesReceived: ["#818CF8", "#4F46E5"],
  newLeads: ["#A21CAF", "#701A75"],
  avgAttendance: ["#C026D3", "#86198F"],
  avgWait: ["#DB2777", "#9D174D"],
};

const formatMinutes = (minutes) => {
  const total = Math.max(0, Math.round(minutes || 0));
  const hours = String(Math.floor(total / 60)).padStart(2, "0");
  const mins = String(total % 60).padStart(2, "0");
  return `${hours}h ${mins}m`;
};

const toInputDate = (date) => format(date, "yyyy-MM-dd");

const StatCard = ({ name, value, icon: Icon }) => {
  const classes = useStyles();
  const [from, to] = gradients[name];

  return (
    <Paper
      className={classes.card}
      style={{ background: `linear-gradient(135deg, ${from}, ${to})` }}
    >
      <Typography component="h3" className={classes.cardTitle}>
        {i18n.t(`dashboard.cards.${name}`)}
      </Typography>
      <Typography component="p" className={classes.cardValue}>
        {value}
      </Typography>
      <Icon className={classes.cardIcon} />
    </Paper>
  );
};

const Dashboard = () => {
  const classes = useStyles();
  const today = toInputDate(new Date());
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);
  const [data, setData] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const refreshTimer = useRef(null);

  const fetchData = useCallback(async () => {
    if (!dateFrom || !dateTo) return;
    try {
      const { data } = await api.get("/dashboard", {
        params: {
          dateFrom: startOfDay(parseISO(dateFrom)).toISOString(),
          dateTo: endOfDay(parseISO(dateTo)).toISOString(),
        },
      });
      setData(data);
    } catch (err) {
      toastError(err);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Informativos ativos mais recentes no topo do painel
  const fetchAnnouncements = useCallback(async () => {
    try {
      const { data } = await api.get("/announcements");
      setAnnouncements(data.filter((a) => a.active).slice(0, 3));
    } catch (err) {
      setAnnouncements([]);
    }
  }, []);

  useEffect(() => {
    fetchAnnouncements();
  }, [fetchAnnouncements]);

  useEffect(() => {
    const socket = openSocket();
    // Agrupa rajadas de eventos em uma única atualização
    const scheduleRefresh = () => {
      clearTimeout(refreshTimer.current);
      refreshTimer.current = setTimeout(fetchData, 1500);
    };

    socket.on("connect", () => socket.emit("joinNotification"));
    socket.on("ticket", scheduleRefresh);
    socket.on("appMessage", scheduleRefresh);
    socket.on("contact", scheduleRefresh);
    socket.on("userOnline", scheduleRefresh);
    socket.on("announcement", fetchAnnouncements);

    return () => {
      clearTimeout(refreshTimer.current);
      socket.disconnect();
    };
  }, [fetchData, fetchAnnouncements]);

  const setLastDays = (days) => {
    const now = new Date();
    setDateFrom(toInputDate(subDays(now, days - 1)));
    setDateTo(toInputDate(now));
  };

  const cards = data
    ? [
        { name: "inAttendance", value: data.inAttendance, icon: PhoneInTalkIcon },
        { name: "waiting", value: data.waiting, icon: HourglassEmptyIcon },
        {
          name: "onlineUsers",
          value: `${data.onlineUsers}/${data.totalUsers}`,
          icon: HeadsetMicIcon,
        },
        { name: "closed", value: data.closed, icon: CheckCircleIcon },
        { name: "messagesSent", value: data.messagesSent, icon: SendIcon },
        {
          name: "messagesReceived",
          value: data.messagesReceived,
          icon: CallReceivedIcon,
        },
        { name: "newLeads", value: data.newLeads, icon: GroupAddIcon },
        {
          name: "avgAttendance",
          value: formatMinutes(data.avgAttendanceMinutes),
          icon: AlarmOnIcon,
        },
        {
          name: "avgWait",
          value: formatMinutes(data.avgWaitMinutes),
          icon: TimerIcon,
        },
      ]
    : [];

  return (
    <Container maxWidth="lg" className={classes.container}>
      {announcements.length > 0 && (
        <div className={classes.announcements}>
          {announcements.map((a) => (
            <AnnouncementCard key={a.id} announcement={a} compact />
          ))}
        </div>
      )}
      <div className={classes.filters}>
        <ButtonGroup size="small" color="primary">
          <Button onClick={() => setLastDays(1)}>
            {i18n.t("dashboard.period.today")}
          </Button>
          <Button onClick={() => setLastDays(7)}>
            {i18n.t("dashboard.period.last7")}
          </Button>
          <Button onClick={() => setLastDays(30)}>
            {i18n.t("dashboard.period.last30")}
          </Button>
        </ButtonGroup>
        <TextField
          type="date"
          size="small"
          variant="outlined"
          label={i18n.t("dashboard.period.from")}
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
          className={classes.dateField}
        />
        <TextField
          type="date"
          size="small"
          variant="outlined"
          label={i18n.t("dashboard.period.to")}
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
          className={classes.dateField}
        />
      </div>

      <Grid container spacing={3}>
        {cards.map((card) => (
          <Grid item xs={12} sm={6} md={4} key={card.name}>
            <StatCard {...card} />
          </Grid>
        ))}

        <Grid item xs={12}>
          <Paper className={classes.paper}>
            <Typography
              component="h2"
              variant="h6"
              color="primary"
              className={classes.sectionTitle}
            >
              {i18n.t("dashboard.attendants.title")}
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{i18n.t("dashboard.attendants.name")}</TableCell>
                  <TableCell align="center">
                    {i18n.t("dashboard.attendants.closed")}
                  </TableCell>
                  <TableCell align="center">
                    {i18n.t("dashboard.attendants.avg")}
                  </TableCell>
                  <TableCell align="center">
                    {i18n.t("dashboard.attendants.status")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.attendants.map((attendant) => (
                  <TableRow key={attendant.id}>
                    <TableCell>{attendant.name}</TableCell>
                    <TableCell align="center">
                      {attendant.closedTickets}
                    </TableCell>
                    <TableCell align="center">
                      {formatMinutes(attendant.avgAttendanceMinutes)}
                    </TableCell>
                    <TableCell align="center">
                      <span
                        className={`${classes.statusDot} ${
                          attendant.online ? classes.online : classes.offline
                        }`}
                      />
                      {i18n.t(
                        `dashboard.attendants.${
                          attendant.online ? "online" : "offline"
                        }`
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper className={classes.chartPaper}>
            <Chart />
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Dashboard;
